import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, RefreshCw } from 'lucide-react';
import AgentToolCard from './AgentToolCard';
import McpSetupModal from './McpSetupModal';
import { useDarkMode } from '../contexts/DarkModeContext';
import { copyToClipboard } from '../utils/clipboard';
import {
  createSession,
  listSessions,
  listSessionsPage,
  getSession,
  deleteSession,
  sendMessage,
  respondToSupervisor,
  finishSession as finishAgentSession,
  stopSession as stopAgentSession,
  connectStream,
  getLatestModels,
  uploadFiles,
  getWorkspaceFiles,
  getWorkspaceFile,
  setCostLimit,
  updateRuntime,
  getApiKeyStatus,
  setApiKey,
  type AgentSession,
  type ListAgentSessionsOptions,
  type SSEEvent,
  type SupervisorQuestion,
  type ModelInfo,
  type ModelVersions,
  type WorkspaceFile,
  type ApiKeyStatus,
} from '../services/agentService';

// ============================================
// Types
// ============================================

interface AgentChatProps {
  sessionId?: string;
  isPublicView?: boolean;
  shareToken?: string;
  token?: string;
  preferredSessionId?: string | null;
  onPreferredSessionHandled?: () => void;
  sessionListOptions?: ListAgentSessionsOptions;
  sessionListLabel?: string | null;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: ToolCallInfo[];
  supervisorQuestion?: SupervisorQuestion;
  timestamp: Date;
}

interface ToolCallInfo {
  id: string;
  toolName: string;
  toolInput?: Record<string, any>;
  success?: boolean;
  duration_ms?: number;
  outputPreview?: string;
  isRunning: boolean;
}

interface CostInfo {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

const REASONING_OPTIONS = ['none', 'minimal', 'low', 'medium', 'high', 'xhigh'] as const;
type ReasoningEffort = typeof REASONING_OPTIONS[number];

const FALLBACK_AGENT_MODELS: ModelInfo[] = [
  { id: 'opus', name: 'Opus', description: 'Most intelligent', tier: 'opus' },
  { id: 'sonnet', name: 'Sonnet', description: 'Fast & capable', tier: 'sonnet' },
];

function formatModelLabel(model: string) {
  if (model.includes('opus')) return 'Opus';
  if (model.includes('sonnet')) return 'Sonnet';
  return model.split('/').pop() || model;
}

function buildModelSelectOptions(modelVersions: ModelVersions | null, extraModelIds: Array<string | null | undefined> = []): ModelInfo[] {
  const options = new Map<string, ModelInfo>();
  const latestModels = modelVersions?.models ?? [];
  const seedOptions = latestModels.length > 0 ? latestModels : FALLBACK_AGENT_MODELS;

  for (const model of seedOptions) {
    if (!model?.id) continue;
    options.set(model.id, model);
  }

  for (const extraModelId of extraModelIds) {
    const id = String(extraModelId || '').trim();
    if (!id || options.has(id)) continue;
    const lower = id.toLowerCase();
    options.set(id, {
      id,
      name: formatModelLabel(id),
      description: '',
      tier: lower.includes('opus') ? 'opus' : 'sonnet',
    });
  }

  return [...options.values()];
}

// ============================================
// Component
// ============================================

const AgentChat: React.FC<AgentChatProps> = ({
  sessionId,
  isPublicView = false,
  shareToken,
  token,
  preferredSessionId,
  onPreferredSessionHandled,
  sessionListOptions,
  sessionListLabel,
}) => {
  const { isDarkMode } = useDarkMode();
  // State
  const [sessions, setSessions] = useState<AgentSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isAgentRunning, setIsAgentRunning] = useState(false);
  const [costInfo, setCostInfo] = useState<CostInfo>({ inputTokens: 0, outputTokens: 0, costUsd: 0 });
  const [modelVersions, setModelVersions] = useState<ModelVersions | null>(null);
  const [activeModel, setActiveModel] = useState<string>('');
  const [selectedModel, setSelectedModel] = useState<string>('');
  const [selectedReasoningEffort, setSelectedReasoningEffort] = useState<ReasoningEffort>('high');
  const [activeReasoningEffort, setActiveReasoningEffort] = useState<ReasoningEffort>('high');
  const [pendingSupervisorQ, setPendingSupervisorQ] = useState<SupervisorQuestion | null>(null);
  const [supervisorResponse, setSupervisorResponse] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [workspaceFiles, setWorkspaceFiles] = useState<WorkspaceFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [apiKeyStatus, setApiKeyStatus] = useState<ApiKeyStatus | null>(null);
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [showMcpModal, setShowMcpModal] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [agentStartTime, setAgentStartTime] = useState<Date | null>(null);
  const [lastAgentEventAt, setLastAgentEventAt] = useState<number | null>(null);
  const [streamWarning, setStreamWarning] = useState<string>('');
  const [isScrollSynced, setIsScrollSynced] = useState(true);
  const [isUpdatingRuntime, setIsUpdatingRuntime] = useState(false);

  // Refs
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const currentAssistantMsgRef = useRef<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isProgrammaticScrollRef = useRef(false);

  // ============================================
  // Effects
  // ============================================

  // Load model versions once
  useEffect(() => {
    getLatestModels(token || undefined).then(setModelVersions).catch(console.error);
    getApiKeyStatus(token || undefined).then(setApiKeyStatus).catch(console.error);
  }, [token]);

  useEffect(() => {
    if (selectedModel || !modelVersions) return;
    setSelectedModel(modelVersions.opus || modelVersions.models?.[0]?.id || '');
  }, [modelVersions, selectedModel]);

  const newSessionModelOptions = useMemo(
    () => buildModelSelectOptions(modelVersions, [selectedModel]),
    [modelVersions, selectedModel]
  );

  const runtimeModelOptions = useMemo(
    () => buildModelSelectOptions(modelVersions, [activeModel]),
    [activeModel, modelVersions]
  );
  const newSessionModelValue = newSessionModelOptions.some((model) => model.id === selectedModel)
    ? selectedModel
    : newSessionModelOptions[0]?.id || '';
  const runtimeModelValue = runtimeModelOptions.some((model) => model.id === activeModel)
    ? activeModel
    : runtimeModelOptions[0]?.id || '';

  const isNearBottom = useCallback(() => {
    if (!messagesContainerRef.current) return true;
    const container = messagesContainerRef.current;
    const threshold = 100;
    return container.scrollTop + container.clientHeight >= container.scrollHeight - threshold;
  }, []);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'auto') => {
    if (!messagesContainerRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior });
      return;
    }

    messagesContainerRef.current.scrollTo({
      top: messagesContainerRef.current.scrollHeight,
      behavior,
    });
  }, []);

  const handleMessagesScroll = useCallback(() => {
    const nearBottom = isNearBottom();
    if (nearBottom) {
      isProgrammaticScrollRef.current = false;
      setIsScrollSynced(true);
      return;
    }
    if (!isProgrammaticScrollRef.current) {
      setIsScrollSynced(false);
    }
  }, [isNearBottom]);

  const handleResyncScroll = useCallback(() => {
    isProgrammaticScrollRef.current = true;
    setIsScrollSynced(true);
    scrollToBottom('smooth');
  }, [scrollToBottom]);

  // Auto-scroll on new messages only while synced
  useEffect(() => {
    if (!isScrollSynced) return;
    scrollToBottom();
  }, [isScrollSynced, messages, scrollToBottom]);

  // Clean up EventSource on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    if (!isAgentRunning) return;

    const interval = window.setInterval(() => {
      if (!lastAgentEventAt) return;
      if (Date.now() - lastAgentEventAt <= 45000) return;
      setStatusMessage(prev => (
        prev && prev.includes('reconnect')
          ? prev
          : 'Still connected; waiting for agent activity...'
      ));
    }, 15000);

    return () => window.clearInterval(interval);
  }, [isAgentRunning, lastAgentEventAt]);

  // ============================================
  // Session Management
  // ============================================

  const loadSessions = useCallback(async () => {
    try {
      const result = sessionListOptions
        ? await listSessionsPage(sessionListOptions, token || undefined)
        : await listSessions(sessionId, token || undefined);
      setSessions(Array.isArray(result) ? result : result.sessions);
      setError(prev => (prev === 'Failed to load agent sessions' ? null : prev));
    } catch (err) {
      console.error('Failed to load sessions:', err);
      setError('Failed to load agent sessions');
    }
  }, [sessionId, sessionListOptions, token]);

  const refreshSelectedSession = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      const session = await getSession(activeSessionId, token || undefined);
      setSessions(prev => (
        prev.some((existing) => existing.id === session.id)
          ? prev.map((existing) => existing.id === session.id ? { ...existing, ...session } : existing)
          : [session, ...prev]
      ));
      setActiveModel(session.model);
      setActiveReasoningEffort((session.reasoning_effort as ReasoningEffort) || 'high');
      setCostInfo({
        inputTokens: session.total_input_tokens || 0,
        outputTokens: session.total_output_tokens || 0,
        costUsd: session.total_cost_usd || 0,
      });
    } catch (err) {
      console.error('Failed to refresh selected session:', err);
    }
  }, [activeSessionId, token]);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // Auto-refresh session list every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeSessionId) {
        void refreshSelectedSession();
        return;
      }
      void loadSessions();
    }, 10000);
    return () => clearInterval(interval);
  }, [activeSessionId, loadSessions, refreshSelectedSession]);

  useEffect(() => {
    const refresh = () => {
      if (activeSessionId) {
        void refreshSelectedSession();
        return;
      }
      void loadSessions();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [activeSessionId, loadSessions, refreshSelectedSession]);

  const handleCreateSession = async () => {
    try {
      setError(null);
      const session = await createSession(sessionId, selectedModel || undefined, selectedReasoningEffort, token || undefined);
      setSessions(prev => [session, ...prev]);
      await openSession(session);
    } catch (err: any) {
      setError(err.message || 'Failed to create session');
    }
  };

  const handleDeleteSession = async (sid: string) => {
    try {
      await deleteSession(sid, token || undefined);
      setSessions(prev => prev.filter(s => s.id !== sid));
      if (activeSessionId === sid) {
        disconnectStream();
        setActiveSessionId(null);
        setMessages([]);
        setCostInfo({ inputTokens: 0, outputTokens: 0, costUsd: 0 });
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete session');
    }
  };

  // ============================================
  // SSE Stream
  // ============================================

  const connectToSession = (sid: string, model: string) => {
    disconnectStream();
    setActiveSessionId(sid);
    setActiveModel(model);
    setIsScrollSynced(true);
    currentAssistantMsgRef.current = '';

    const es = connectStream(sid, handleSSEEvent, (err) => {
      console.error('SSE error:', err);
      setStreamWarning('Stream interrupted; waiting for reconnect...');
      if (isAgentRunning) {
        setStatusMessage('Stream interrupted; waiting for reconnect...');
      }
    }, token || undefined);
    eventSourceRef.current = es;
  };

  const disconnectStream = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsScrollSynced(true);
  };

  const rebuildMessages = useCallback((session: AgentSession): ChatMessage[] => {
    const rebuilt: ChatMessage[] = [];
    if (!session.messages) return rebuilt;

    for (const msg of session.messages) {
      const msgTime = new Date(msg.created_at.endsWith('Z') ? msg.created_at : `${msg.created_at}Z`);
      if (msg.role === 'user') {
        rebuilt.push({
          id: `msg-${msg.id}`,
          role: 'user',
          content: msg.content,
          timestamp: msgTime,
        });
        continue;
      }

      if (msg.role !== 'assistant') {
        continue;
      }

      try {
        const blocks = JSON.parse(msg.content);
        let text = '';
        const toolCalls: ToolCallInfo[] = [];
        for (const block of blocks) {
          if (block.type === 'text') text += block.text;
          else if (block.type === 'tool_use') {
            toolCalls.push({
              id: block.id,
              toolName: block.name,
              toolInput: block.input,
              isRunning: false,
              success: true,
            });
          }
        }
        rebuilt.push({
          id: `msg-${msg.id}`,
          role: 'assistant',
          content: text,
          toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
          timestamp: msgTime,
        });
      } catch {
        rebuilt.push({
          id: `msg-${msg.id}`,
          role: 'assistant',
          content: msg.content,
          timestamp: msgTime,
        });
      }
    }

    return rebuilt;
  }, []);

  const openSession = useCallback(async (sessionOrId: AgentSession | string) => {
    const session = typeof sessionOrId === 'string'
      ? await getSession(sessionOrId, token || undefined)
      : sessionOrId;

    setSessions(prev => (
      prev.some((existing) => existing.id === session.id)
        ? prev.map((existing) => existing.id === session.id ? { ...existing, ...session } : existing)
        : [session, ...prev]
    ));
    connectToSession(session.id, session.model);
    setActiveReasoningEffort((session.reasoning_effort as ReasoningEffort) || 'high');
    setMessages(rebuildMessages(session));
    setCostInfo({
      inputTokens: session.total_input_tokens || 0,
      outputTokens: session.total_output_tokens || 0,
      costUsd: session.total_cost_usd || 0,
    });
    return session;
  }, [rebuildMessages, token]);

  // Refresh on navigate-to-agent custom event (e.g., from transcript refinement redirect)
  useEffect(() => {
    const handleNavigateToAgent = async (event: Event) => {
      const agentSessionId = (event as CustomEvent).detail?.agentSessionId;

      if (agentSessionId) {
        try {
          await openSession(agentSessionId);
          await loadSessions();
          return;
        } catch (err) {
          console.error('Failed to auto-open agent session:', err);
        }
      }

      await loadSessions();
    };
    window.addEventListener('navigate-to-agent', handleNavigateToAgent);
    return () => window.removeEventListener('navigate-to-agent', handleNavigateToAgent);
  }, [loadSessions, openSession]);

  useEffect(() => {
    if (!preferredSessionId) return;

    let cancelled = false;
    (async () => {
      try {
        const session = await getSession(preferredSessionId, token || undefined);
        if (cancelled) return;
        await openSession(session);
      } catch (err) {
        console.error('Failed to open preferred agent session:', err);
      } finally {
        if (!cancelled) {
          onPreferredSessionHandled?.();
          void loadSessions();
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [preferredSessionId, token, loadSessions, onPreferredSessionHandled, openSession]);

  const handleSelectSession = async (sid: string) => {
    try {
      setError(null);
      await openSession(sid);
    } catch (err: any) {
      setError(err.message || 'Failed to load session');
    }
  };

  const refreshWorkspaceFiles = useCallback(async () => {
    if (!activeSessionId) return;
    try {
      const files = await getWorkspaceFiles(activeSessionId, token || undefined);
      setWorkspaceFiles(files);
    } catch {
      // Workspace may not exist yet
    }
  }, [activeSessionId, token]);

  // Refresh workspace files when session changes
  useEffect(() => {
    refreshWorkspaceFiles();
  }, [refreshWorkspaceFiles]);

  const handleSSEEvent = useCallback((event: SSEEvent) => {
    setLastAgentEventAt(Date.now());
    if (event.type !== 'ping') {
      setStreamWarning('');
    }

    switch (event.type) {
      case 'connected':
        setActiveModel(event.model || '');
        if (event.reasoningEffort) {
          setActiveReasoningEffort(event.reasoningEffort as ReasoningEffort);
        }
        break;

      case 'status': {
        setIsAgentRunning(true);
        if (!agentStartTime) setAgentStartTime(new Date());
        if (event.message) setStatusMessage(event.message);
        break;
      }

      case 'session_update': {
        // Update sidebar when title/metadata changes
        if (event.title) {
          setSessions(prev => prev.map(s =>
            s.id === activeSessionId ? { ...s, title: event.title } : s
          ));
        }
        if (event.model) {
          setActiveModel(event.model);
        }
        if (event.reasoningEffort) {
          setActiveReasoningEffort(event.reasoningEffort as ReasoningEffort);
        }
        break;
      }

      case 'text': {
        currentAssistantMsgRef.current += event.content;
        setIsAgentRunning(true);
        setStatusMessage('Responding...');
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last && last.role === 'assistant' && last.id === 'streaming') {
            return [...prev.slice(0, -1), { ...last, content: currentAssistantMsgRef.current }];
          }
          return [...prev, {
            id: 'streaming',
            role: 'assistant',
            content: currentAssistantMsgRef.current,
            timestamp: new Date(),
          }];
        });
        break;
      }

      case 'tool_start': {
        setStatusMessage(`Running ${event.toolName}...`);
        const toolCall: ToolCallInfo = {
          id: event.toolCallId || `tc-${Date.now()}`,
          toolName: event.toolName,
          toolInput: event.toolInput,
          isRunning: true,
        };
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last && last.role === 'assistant' && last.id === 'streaming') {
            const existing = last.toolCalls || [];
            return [...prev.slice(0, -1), { ...last, toolCalls: [...existing, toolCall] }];
          }
          return [...prev, {
            id: 'streaming',
            role: 'assistant',
            content: currentAssistantMsgRef.current,
            toolCalls: [toolCall],
            timestamp: new Date(),
          }];
        });
        break;
      }

      case 'tool_end': {
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last && last.role === 'assistant' && last.toolCalls) {
            const updatedCalls = last.toolCalls.map(tc =>
              ((tc.id === event.toolCallId) || (tc.toolName === event.toolName && tc.isRunning))
                ? { ...tc, isRunning: false, success: event.success, duration_ms: event.duration_ms, outputPreview: event.outputPreview }
                : tc
            );
            return [...prev.slice(0, -1), { ...last, toolCalls: updatedCalls }];
          }
          return prev;
        });
        break;
      }

      case 'ask_supervisor': {
        setIsAgentRunning(false);
        setPendingSupervisorQ({
          id: event.id,
          question: event.question,
          options: event.options || [],
          context: event.context || '',
        });
        break;
      }

      case 'cost_update': {
        const newCost = event.costUsd || 0;
        setCostInfo({
          inputTokens: event.inputTokens || 0,
          outputTokens: event.outputTokens || 0,
          costUsd: newCost,
        });
        // Update sidebar cost in real-time
        setSessions(prev => prev.map(s =>
          s.id === activeSessionId ? { ...s, total_cost_usd: newCost } : s
        ));
        break;
      }

      case 'done': {
        // Calculate thinking duration before clearing
        const thinkDuration = agentStartTime ? Math.floor((Date.now() - agentStartTime.getTime()) / 1000) : 0;
        setIsAgentRunning(false);
        setIsLoading(false);
        setStatusMessage('');
        setAgentStartTime(null);
        setLastAgentEventAt(null);
        setStreamWarning('');
        currentAssistantMsgRef.current = '';
        // Finalize streaming message and append timing summary
        setMessages(prev => {
          const updated = [...prev];
          const lastIdx = updated.length - 1;
          if (lastIdx >= 0 && updated[lastIdx].id === 'streaming') {
            const thinkText = thinkDuration > 0
              ? `\n\n*Thought for ${Math.floor(thinkDuration / 60)}m ${thinkDuration % 60}s*`
              : '';
            updated[lastIdx] = {
              ...updated[lastIdx],
              id: `msg-${Date.now()}`,
              content: updated[lastIdx].content + thinkText,
            };
          }
          return updated;
        });
        if (event.totalCost !== undefined) {
          setCostInfo(prev => ({ ...prev, costUsd: event.totalCost }));
        }
        // Refresh workspace files and session list after agent finishes
        refreshWorkspaceFiles();
        loadSessions();
        if (sessionId) {
          window.dispatchEvent(new CustomEvent('rescript-session-refresh-needed', {
            detail: { sessionId, scopes: ['agent_done'] },
          }));
        }
        break;
      }

      case 'error': {
        setIsAgentRunning(false);
        setIsLoading(false);
        setStatusMessage('');
        setAgentStartTime(null);
        setLastAgentEventAt(null);
        setStreamWarning('');
        setError(event.message || 'Unknown error');
        currentAssistantMsgRef.current = '';
        break;
      }

      case 'ping':
        break;

      case 'workspace_updated':
        refreshWorkspaceFiles();
        break;

      case 'comments_updated':
        if (sessionId) {
          window.dispatchEvent(new CustomEvent('rescript-session-refresh-needed', {
            detail: { sessionId, scopes: ['comments'] },
          }));
        }
        window.dispatchEvent(new CustomEvent('agent-comments-updated', {
          detail: { sessionId, ...event },
        }));
        break;

      case 'ttml_updated':
        if (sessionId) {
          window.dispatchEvent(new CustomEvent('rescript-session-refresh-needed', {
            detail: { sessionId, scopes: ['metadata', 'description', 'narrative'] },
          }));
        }
        window.dispatchEvent(new CustomEvent('agent-ttml-updated', {
          detail: { sessionId, ...event },
        }));
        break;

      default:
        break;
    }
  }, [refreshWorkspaceFiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================
  // Send Message
  // ============================================

  const handleSendMessage = async () => {
    if (!inputText.trim() || !activeSessionId) return;

    const text = inputText.trim();
    setInputText('');
    setError(null);
    setIsLoading(true);
    setIsAgentRunning(true);
    setAgentStartTime(new Date());
    setLastAgentEventAt(Date.now());
    setStreamWarning('');
    setIsScrollSynced(true);
    currentAssistantMsgRef.current = '';

    // Add user message
    setMessages(prev => [...prev, {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
    }]);

    try {
      const result = await sendMessage(activeSessionId, text, token || undefined);
      if (result.queued) {
        setStatusMessage(`Queued message (${result.queueLength || 1} waiting)...`);
        setIsLoading(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
      setIsLoading(false);
      setIsAgentRunning(false);
    }
  };

  const handleSupervisorRespond = async () => {
    if (!pendingSupervisorQ || !activeSessionId || !supervisorResponse.trim()) return;

    try {
      await respondToSupervisor(activeSessionId, pendingSupervisorQ.id, supervisorResponse.trim(), token || undefined);
      setPendingSupervisorQ(null);
      setSupervisorResponse('');
      setIsAgentRunning(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send response');
    }
  };

  const handleStopAgent = async () => {
    if (!activeSessionId) return;
    try {
      await stopAgentSession(activeSessionId, token || undefined);
      setIsAgentRunning(false);
      setIsLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to stop agent');
    }
  };

  const handleFinishAgent = async () => {
    if (!activeSessionId) return;
    try {
      await finishAgentSession(activeSessionId, token || undefined);
      setIsAgentRunning(false);
      setIsLoading(false);
      setStatusMessage('');
      await loadSessions();
    } catch (err: any) {
      setError(err.message || 'Failed to finish agent session');
    }
  };

  const handleCopySessionId = useCallback(async () => {
    if (!activeSessionId) return;
    const copied = await copyToClipboard(activeSessionId);
    if (copied) {
      setStatusMessage(`Copied agent session ID: ${activeSessionId}`);
      return;
    }
    setError('Failed to copy agent session ID');
  }, [activeSessionId]);

  const handleFileUpload = async (files: FileList | File[]) => {
    if (!activeSessionId || files.length === 0) return;
    setIsUploading(true);
    setError(null);
    try {
      const fileArray = Array.from(files);
      const uploaded = await uploadFiles(activeSessionId, fileArray, 'context', token || undefined);
      setMessages(prev => [...prev, {
        id: `upload-${Date.now()}`,
        role: 'user',
        content: `Uploaded ${uploaded.length} file${uploaded.length > 1 ? 's' : ''} to workspace: ${uploaded.map(f => f.originalName).join(', ')}`,
        timestamp: new Date(),
      }]);
      refreshWorkspaceFiles();
    } catch (err: any) {
      setError(err.message || 'Failed to upload files');
    } finally {
      setIsUploading(false);
      setIsDragOver(false);
    }
  };

  const handleDownloadFile = async (filePath: string) => {
    if (!activeSessionId) return;
    try {
      const blob = await getWorkspaceFile(activeSessionId, filePath);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filePath.split('/').pop() || filePath;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download file');
    }
  };

  const handleUpdateRuntime = async (updates: { model?: string; reasoningEffort?: ReasoningEffort }) => {
    if (!activeSessionId) return;
    try {
      setError(null);
      setIsUpdatingRuntime(true);
      const result = await updateRuntime(activeSessionId, updates, token || undefined);
      if (updates.model) {
        setActiveModel(result.model);
      }
      if (updates.reasoningEffort) {
        setActiveReasoningEffort(result.reasoningEffort as ReasoningEffort);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update agent runtime');
    } finally {
      setIsUpdatingRuntime(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (activeSessionId) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (activeSessionId && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (pendingSupervisorQ) {
        handleSupervisorRespond();
      } else {
        handleSendMessage();
      }
    }
  };

  // ============================================
  // Render
  // ============================================

  const formatCost = (usd: number) => {
    if (usd < 0.01) return `$${(usd * 100).toFixed(2)}c`;
    return `$${usd.toFixed(2)}`;
  };

  const getRunnerBadge = (session: AgentSession) => {
    switch (session.runner_status) {
      case 'queued':
        return { label: 'Queued', className: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' };
      case 'claimed':
      case 'starting':
        return { label: 'Starting', className: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' };
      case 'running':
        return { label: 'Running', className: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' };
      case 'sticky':
        return { label: 'Waiting', className: 'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300' };
      case 'failed':
        return { label: 'Failed', className: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' };
      default:
        return null;
    }
  };

  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 5) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const formatElapsed = (date: Date) => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`;
  };

  const formatSessionContext = (session: AgentSession) => {
    const parts: string[] = [];
    if (session.schedule_id) parts.push(`sched ${session.schedule_id.slice(0, 8)}`);
    if (session.trigger_type) {
      parts.push(session.trigger_event ? `${session.trigger_type}:${session.trigger_event}` : session.trigger_type);
    }
    if (session.session_id) parts.push(`episode ${session.session_id.slice(0, 8)}`);
    return parts.join(' • ');
  };

  // Timer tick for live-updating timestamps
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const activeSession = activeSessionId
    ? sessions.find((session) => session.id === activeSessionId) || null
    : null;

  return (
    <div
      className={`relative flex h-full max-h-full min-h-0 w-full min-w-0 overflow-hidden overscroll-none bg-[#F8F6F1] dark:bg-[#101722] ${isDragOver ? 'ring-2 ring-inset ring-[#FFB52B]' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Sidebar */}
      {sidebarOpen && (
        <div className="flex min-h-0 w-64 max-w-full shrink-0 flex-col overflow-hidden border-r border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
          {/* New Session Button + Model Selector */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="mb-2 space-y-2">
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">New Session Model</div>
                <select
                  value={newSessionModelValue}
                  onChange={(event) => setSelectedModel(event.target.value)}
                  disabled={newSessionModelOptions.length === 0}
                  className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                >
                  {newSessionModelOptions.length === 0 ? (
                    <option value="">Loading models...</option>
                  ) : (
                    newSessionModelOptions.map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.tier === 'opus' ? 'Opus' : model.tier === 'sonnet' ? 'Sonnet' : model.name}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div>
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Reasoning</div>
                <select
                  value={selectedReasoningEffort}
                  onChange={(event) => setSelectedReasoningEffort(event.target.value as ReasoningEffort)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                >
                  {REASONING_OPTIONS.map((effort) => (
                    <option key={effort} value={effort}>
                      {effort}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <button
              onClick={handleCreateSession}
              className="w-full rounded-lg bg-[#FFB52B] px-3 py-2 text-sm font-medium text-[#232E3F] transition-colors hover:bg-[#FFC54D]"
            >
              + New Agent Session
            </button>
            {/* BYOK API Key */}
            <div className="mt-2">
              {showApiKeyInput ? (
                <div className="flex flex-col gap-1">
                  <input
                    type="password"
                    value={apiKeyInput}
                    onChange={e => setApiKeyInput(e.target.value)}
                    placeholder="sk-ant-api03-..."
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded"
                  />
                  <div className="flex gap-1">
                    <button
                      onClick={async () => {
                        setApiKeyLoading(true);
                        try {
                          const result = await setApiKey(apiKeyInput, token || undefined);
                          setApiKeyStatus({ hasKey: result.hasKey, keyPreview: result.hasKey ? `...${apiKeyInput.slice(-6)}` : null });
                          setShowApiKeyInput(false);
                          setApiKeyInput('');
                        } catch (e: any) {
                          setError(e.message || 'Invalid API key');
                        } finally {
                          setApiKeyLoading(false);
                        }
                      }}
                      disabled={apiKeyLoading}
                      className="flex-1 rounded bg-[#FFB52B] px-2 py-1 text-[10px] text-[#232E3F] hover:bg-[#FFC54D] disabled:opacity-50"
                    >
                      {apiKeyLoading ? 'Validating...' : 'Save'}
                    </button>
                    <button
                      onClick={() => { setShowApiKeyInput(false); setApiKeyInput(''); }}
                      className="px-2 py-1 text-[10px] bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowApiKeyInput(true)}
                  className="w-full text-left px-2 py-1 text-[10px] text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                >
                  {apiKeyStatus?.hasKey
                    ? `API Key: ${apiKeyStatus.keyPreview} (change)`
                    : 'Add your Anthropic API key (BYOK)'}
                </button>
              )}
            </div>
          </div>

          {/* Session List */}
          <div className="px-3 py-1.5 border-b border-gray-200/50 dark:border-gray-700/50">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Sessions</span>
              <button
                onClick={() => loadSessions()}
                className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                title="Refresh sessions"
              >
                <RefreshCw size={12} />
              </button>
            </div>
            {sessionListLabel && (
              <div className="mt-1 text-[10px] text-gray-500 dark:text-gray-400 truncate">
                {sessionListLabel}
              </div>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
            {sessions.length === 0 ? (
              <div className="p-4 text-center text-gray-400 dark:text-gray-500 text-sm">
                No sessions yet
              </div>
            ) : (
              sessions.map(s => (
                (() => {
                  const runnerBadge = getRunnerBadge(s);
                  return (
                <div
                  key={s.id}
                  className={`p-3 border-b border-gray-200/50 dark:border-gray-700/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors ${
                    activeSessionId === s.id ? 'bg-[#FFF8EA] dark:bg-[#232E3F]/70 border-l-4 border-l-[#FFB52B]' : ''
                  }`}
                  onClick={() => handleSelectSession(s.id)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate flex-1">
                      {s.title || 'Untitled'}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteSession(s.id); }}
                      className="text-gray-400 hover:text-red-500 text-xs ml-1"
                      title="Delete session"
                    >
                      &#x2715;
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      s.status === 'active' ? 'bg-[#FFF4D8] dark:bg-[#332A17] text-[#8B5B00] dark:text-[#FFE3A1]' :
                      s.status === 'stopped' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' :
                      'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}>
                      {formatModelLabel(s.model)}
                    </span>
                    {runnerBadge && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${runnerBadge.className}`}>
                        {runnerBadge.label}
                      </span>
                    )}
                    {s.total_cost_usd > 0 && (
                      <span className="text-[10px] text-gray-400">
                        {formatCost(s.total_cost_usd)}
                      </span>
                    )}
                    {isAgentRunning && s.id === activeSessionId && (
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FFCF74] opacity-75"></span>
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#FFB52B]"></span>
                      </span>
                    )}
                  </div>
                  {formatSessionContext(s) && (
                    <div className="mt-1 text-[10px] text-gray-400 dark:text-gray-500 truncate">
                      {formatSessionContext(s)}
                    </div>
                  )}
                </div>
                  );
                })()
              ))
            )}
          </div>

          {/* Workspace Files (clickable for download) */}
          {activeSessionId && workspaceFiles.length > 0 && (
            <div className="max-h-40 shrink-0 overflow-y-auto overscroll-y-contain border-t border-gray-200 dark:border-gray-700">
              <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Workspace Files
              </div>
              {workspaceFiles.map(f => (
                <div
                  key={f.path}
                  className="px-3 py-1 text-[10px] text-gray-500 dark:text-gray-400 truncate hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer flex items-center gap-1"
                  title={`Click to download: ${f.path}`}
                  onClick={() => handleDownloadFile(f.path)}
                >
                  <svg className="w-3 h-3 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span className="truncate">{f.path}</span>
                  <span className="text-gray-400 flex-shrink-0">({(f.size / 1024).toFixed(1)}KB)</span>
                </div>
              ))}
            </div>
          )}

          {/* Status & Model Info */}
          <div className="shrink-0 border-t border-gray-200 p-3 text-[10px] text-gray-400 dark:border-gray-700 dark:text-gray-500">
            {isAgentRunning && (statusMessage || streamWarning) && (
              <div className="mb-1 flex items-center gap-1.5 font-medium text-[#B57700] dark:text-[#FFCF74]">
                <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FFCF74] opacity-75"></span>
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#FFB52B]"></span>
                </span>
                <span className="truncate">{statusMessage || streamWarning}</span>
              </div>
            )}
            {activeModel && <div>Session: {formatModelLabel(activeModel)}</div>}
          </div>
        </div>
      )}

      {/* Drag overlay */}
      {isDragOver && activeSessionId && (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-lg border-2 border-dashed border-[#FFB52B] bg-[#FFF4D8]/60 dark:bg-[#232E3F]/70">
          <div className="rounded-[1.25rem] border border-[#E7B14C]/45 bg-white/95 px-6 py-4 text-center shadow-lg dark:border-[#79613A] dark:bg-[#1E2634]">
            <div className="text-2xl mb-1">{'\u{1F4C1}'}</div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Drop files to upload to workspace</div>
          </div>
        </div>
      )}

      <McpSetupModal
        isDarkMode={isDarkMode}
        isOpen={showMcpModal}
        onClose={() => setShowMcpModal(false)}
        title="MLST ReScript MCP"
      />

      {/* Main Chat Area */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="shrink-0 overflow-hidden border-b border-gray-200 bg-gray-50/50 px-4 py-2 dark:border-gray-700 dark:bg-gray-800/30">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Toggle sidebar"
          >
            {sidebarOpen ? '\u25C0' : '\u25B6'}
          </button>
          {activeModel && (
            <span className="rounded-full bg-[#232E3F] px-2 py-0.5 text-xs font-medium text-[#F4F4F4] dark:bg-[#1B2534] dark:text-[#FFE3A1]">
              {formatModelLabel(activeModel)}
            </span>
          )}
          {costInfo.costUsd > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formatCost(costInfo.costUsd)} ({costInfo.inputTokens.toLocaleString()} in / {costInfo.outputTokens.toLocaleString()} out)
            </span>
          )}
          {isAgentRunning && (
            <span className="flex items-center gap-1.5 text-xs text-[#B57700] dark:text-[#FFCF74]">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FFCF74] opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FFB52B]"></span>
              </span>
              {statusMessage || streamWarning || 'Working...'}
              {agentStartTime && (
                <span className="text-gray-400 dark:text-gray-500 tabular-nums">{formatElapsed(agentStartTime)}</span>
              )}
            </span>
          )}
          <div className="min-w-0 flex-1" />
          {activeSessionId && messages.length > 0 && !isScrollSynced && (
            <>
              <span className="text-xs px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded font-medium">
                Unsynced
              </span>
              <button
                onClick={handleResyncScroll}
                className="text-xs px-2 py-1 bg-amber-100 hover:bg-amber-200 dark:bg-amber-900/30 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-400 rounded transition-colors"
              >
                Sync
              </button>
            </>
          )}
          {activeSessionId && (
            <>
              <button
                onClick={() => { void handleCopySessionId(); }}
                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded transition-colors inline-flex items-center gap-1"
                title="Copy agent session ID"
              >
                <Copy size={12} />
                <span>Copy ID</span>
              </button>
              <select
                value={runtimeModelValue}
                onChange={(event) => { void handleUpdateRuntime({ model: event.target.value }); }}
                disabled={isUpdatingRuntime || runtimeModelOptions.length === 0}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                title="Model for the next turn"
              >
                {runtimeModelOptions.length === 0 ? (
                  <option value="">Loading models...</option>
                ) : (
                  runtimeModelOptions.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.tier === 'opus' ? 'Opus' : model.tier === 'sonnet' ? 'Sonnet' : model.name}
                    </option>
                  ))
                )}
              </select>
              <select
                value={activeReasoningEffort}
                onChange={(event) => { void handleUpdateRuntime({ reasoningEffort: event.target.value as ReasoningEffort }); }}
                disabled={isUpdatingRuntime}
                className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200"
                title="Reasoning level for the next turn"
              >
                {REASONING_OPTIONS.map((effort) => (
                  <option key={effort} value={effort}>
                    {effort}
                  </option>
                ))}
              </select>
              <button
                onClick={() => setShowMcpModal(true)}
                className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded transition-colors"
                title="Open MLST ReScript MCP setup"
              >
                MCP Setup
              </button>
            </>
          )}
          {isAgentRunning && (
            <button
              onClick={handleStopAgent}
              className="text-xs px-2 py-1 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-700 dark:text-red-400 rounded transition-colors"
            >
              Stop Agent
            </button>
          )}
          {!isAgentRunning && activeSession && activeSession.status !== 'completed' && (
            <button
              onClick={handleFinishAgent}
              className="text-xs px-2 py-1 bg-emerald-100 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-400 rounded transition-colors"
            >
              Finish Session
            </button>
          )}
          </div>
        </div>

        {/* Messages */}
        <div
          ref={messagesContainerRef}
          onScroll={handleMessagesScroll}
          className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-4 space-y-4"
        >
          {!activeSessionId ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-400 dark:text-gray-500">
                <div className="text-2xl mb-2">{'\u{1F916}'}</div>
                <div className="text-sm font-medium mb-1">MLST Agent</div>
                <div className="text-xs">Create a new session to start chatting</div>
              </div>
            </div>
          ) : messages.length === 0 && !isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-400 dark:text-gray-500 max-w-md">
                <div className="text-sm mb-2">What would you like to do?</div>
                <div className="text-xs space-y-1">
                  <div>"Generate metadata for this episode"</div>
                  <div>"Analyze the transcript quality"</div>
                  <div>"Improve the current metadata"</div>
                  <div>"Tell me about this episode"</div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {messages.map(msg => (
                <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] ${
                    msg.role === 'user'
                      ? 'rounded-2xl rounded-br-md bg-[#232E3F] px-4 py-2 text-[#F4F4F4]'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-2xl rounded-bl-md px-4 py-3'
                  }`}>
                    {/* Tool calls FIRST — so streaming text appears below */}
                    {msg.toolCalls && msg.toolCalls.length > 0 && (
                      <div className={msg.content ? 'mb-2' : ''}>
                        {msg.toolCalls.map(tc => (
                          <AgentToolCard
                            key={tc.id}
                            toolName={tc.toolName}
                            toolCallId={tc.id}
                            toolInput={tc.toolInput}
                            success={tc.success}
                            duration_ms={tc.duration_ms}
                            outputPreview={tc.outputPreview}
                            isRunning={tc.isRunning}
                          />
                        ))}
                      </div>
                    )}

                    {/* Text content — markdown for assistant, plain for user */}
                    {msg.content && (
                      msg.role === 'assistant' ? (
                        <div className="text-sm prose prose-sm dark:prose-invert max-w-none break-words
                          prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5
                          prose-headings:my-2 prose-headings:text-base
                          prose-code:text-xs prose-code:bg-gray-200 prose-code:dark:bg-gray-700 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
                          prose-pre:bg-gray-200 prose-pre:dark:bg-gray-700 prose-pre:text-xs prose-pre:p-2 prose-pre:rounded-lg
                          prose-a:text-[#B57700] prose-a:dark:text-[#FFCF74]">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <div className="text-sm whitespace-pre-wrap break-words">{msg.content}</div>
                      )
                    )}
                    {/* Timestamp */}
                    <div className={`text-[10px] mt-1 ${
                      msg.role === 'user' ? 'text-[#FFE3A1]' : 'text-gray-400 dark:text-gray-500'
                    }`}>
                      {formatTimeAgo(msg.timestamp)}
                    </div>
                  </div>
                </div>
              ))}

              {/* Supervisor Question */}
              {pendingSupervisorQ && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-2xl px-4 py-3">
                    <div className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 mb-1">
                      Agent is asking you:
                    </div>
                    <div className="text-sm text-gray-900 dark:text-gray-100 mb-2">
                      {pendingSupervisorQ.question}
                    </div>
                    {pendingSupervisorQ.context && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-2 italic">
                        {pendingSupervisorQ.context}
                      </div>
                    )}
                    {pendingSupervisorQ.options.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {pendingSupervisorQ.options.map((opt, i) => (
                          <button
                            key={i}
                            onClick={() => setSupervisorResponse(opt)}
                            className={`text-xs px-2 py-1 rounded border transition-colors ${
                              supervisorResponse === opt
                                ? 'bg-[#FFF4D8] dark:bg-[#332A17] border-[#FFB52B] text-[#8B5B00] dark:text-[#FFE3A1]'
                                : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={supervisorResponse}
                        onChange={(e) => setSupervisorResponse(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type your response..."
                        className="flex-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/35 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                      />
                      <button
                        onClick={handleSupervisorRespond}
                        disabled={!supervisorResponse.trim()}
                        className="rounded bg-[#FFB52B] px-3 py-1.5 text-sm text-[#232E3F] transition-colors hover:bg-[#FFC54D] disabled:bg-gray-400"
                      >
                        Send
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Activity indicator — always visible when agent is running */}
              {isAgentRunning && !pendingSupervisorQ && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl px-3 py-1.5">
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#FFCF74] opacity-75"></span>
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FFB52B]"></span>
                      </span>
                      <span>{statusMessage || streamWarning || 'Working...'}</span>
                      {agentStartTime && (
                        <span className="text-gray-400 dark:text-gray-500 tabular-nums">{formatElapsed(agentStartTime)}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Error display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2 text-sm text-red-700 dark:text-red-400">
              {error}
              {error.toLowerCase().includes('cost limit') && activeSessionId && (
                <button
                  onClick={async () => {
                    const newLimit = prompt('Set new cost limit (USD):', '50');
                    if (newLimit) {
                      try {
                        await setCostLimit(activeSessionId, parseFloat(newLimit), token || undefined);
                        setError(null);
                      } catch (e: any) {
                        setError(e.message || 'Failed to set cost limit');
                      }
                    }
                  }}
                  className="ml-2 px-2 py-0.5 bg-red-100 dark:bg-red-800 hover:bg-red-200 dark:hover:bg-red-700 rounded text-xs font-medium"
                >
                  Raise Limit
                </button>
              )}
              <button
                onClick={() => setError(null)}
                className="ml-2 text-red-500 hover:text-red-700"
              >
                &#x2715;
              </button>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        {activeSessionId && !pendingSupervisorQ && (
          <div className="shrink-0 overflow-hidden border-t border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900">
            <div className="flex min-w-0 gap-2">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isAgentRunning ? "Queue a follow-up message..." : "Ask the agent to do something..."}
                disabled={!activeSessionId}
                rows={1}
                className={`min-w-0 flex-1 resize-none rounded-lg border-2 bg-white px-3 py-2 text-sm text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FFB52B]/35 dark:bg-gray-800 dark:text-gray-100 ${
                  isAgentRunning
                    ? 'border-[#FFB52B] dark:border-[#FFCF74] animate-pulse'
                    : activeSessionId
                      ? 'border-[#232E3F]/35 dark:border-[#556274]'
                      : 'border-gray-300 dark:border-gray-600'
                }`}
                style={{ minHeight: '38px', maxHeight: '120px' }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = Math.min(target.scrollHeight, 120) + 'px';
                }}
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  if (e.target.files && e.target.files.length > 0) {
                    handleFileUpload(e.target.files);
                    e.target.value = '';
                  }
                }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm rounded-lg transition-colors disabled:opacity-50"
                title="Upload files to workspace"
              >
                {isUploading ? '...' : '\u{1F4CE}'}
              </button>
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim() || !activeSessionId}
                className="rounded-lg bg-[#FFB52B] px-4 py-2 text-sm font-medium text-[#232E3F] transition-colors hover:bg-[#FFC54D] disabled:bg-gray-400 dark:disabled:bg-gray-600"
              >
                {isAgentRunning ? 'Queue' : 'Send'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentChat;
